import { useEffect, useState, useMemo, ChangeEvent } from 'react';
import { TextField, InputLabel, Divider, FormHelperText, Grid, InputAdornment, DialogContent } from '@mui/material';
import { useTranslation } from 'react-i18next';
//import AssignList from 'pages/web/settings/subpages/combinations/AssignList';
import AssignList from 'components/transfer/ListTransfer';
import { TimekeeperService } from 'services/TimekeeperService';

interface OvertimeRule {
    overtime_rule_id: number;
    name: string;
}

interface Zone {
    zone_id: number;
    name: string;
}

interface ScheduleDesviationRule {
    schedule_desviation_rule_id: number;
    name: string;
}

interface CombinationRuleFormData {
    combination_rule_id?: number | string | null;
    name: string;
    description: string;
    overtimeSelect: number[];
    zoneSelect: number[];
    scheduleDesviationSelect: number[];
    overtime_rules: OvertimeRule[];
    zones: Zone[];
    schedule_desviation_rules: ScheduleDesviationRule[];
}

interface CombinationRuleRow {
    id: number;
    name: string;
    description?: string;
    overtime_rules?: OvertimeRule[];
    zones?: Zone[];
    schedule_desviation_rules?: ScheduleDesviationRule[];
}

interface LoadingStates {
    overtime_rules: boolean;
    zones: boolean;
    schedule_desviation_rules: boolean;
}

interface DataStates {
    overtime_rules: OvertimeRule[];
    zones: Zone[];
    schedule_desviation_rules: ScheduleDesviationRule[];
}

interface CombinationRuleFormProps {
    mode?: 'add' | 'edit' | 'duplicate';
    select?: CombinationRuleRow | null;
    rows?: CombinationRuleRow[];
    formData: CombinationRuleFormData;
    setFormData: React.Dispatch<React.SetStateAction<CombinationRuleFormData>>;
    onDataLoaded?: (data: DataStates) => void;
}

interface TransferItem {
    id: number;
    name: string;
}

const tk = new TimekeeperService();

const CombinationRuleForm = ({ mode = 'add', select = null, rows = [], formData, setFormData, onDataLoaded }: CombinationRuleFormProps) => {
    const { t } = useTranslation();
    const [loadingStates, setLoadingStates] = useState<LoadingStates>({
        overtime_rules: true,
        zones: true,
        schedule_desviation_rules: true,
    });

    const [dataStates, setDataStates] = useState<DataStates>({
        overtime_rules: [],
        zones: [],
        schedule_desviation_rules: [],
    });

    const overtimeItems = useMemo<TransferItem[]>(() => dataStates.overtime_rules.map((item) => ({ id: item.overtime_rule_id, name: item.name || '' })), [dataStates.overtime_rules]).sort((a, b) =>
        a.name.localeCompare(b.name),
    );

    const zoneItems = useMemo<TransferItem[]>(() => dataStates.zones.map((item) => ({ id: item.zone_id, name: item.name || '' })).sort((a, b) => a.name.localeCompare(b.name)), [dataStates.zones]);

    const scheduleDesviationItems = useMemo<TransferItem[]>(
        () =>
            dataStates.schedule_desviation_rules.map((item) => ({
                id: item.schedule_desviation_rule_id,
                name: item.name || '',
            })),
        [dataStates.schedule_desviation_rules],
    ).sort((a, b) => a.name.localeCompare(b.name));

    const selectedOvertimeItems = useMemo<TransferItem[]>(() => overtimeItems.filter((item) => formData.overtimeSelect.includes(item.id)), [overtimeItems, formData.overtimeSelect]).sort((a, b) =>
        a.name.localeCompare(b.name),
    );

    const selectedZoneItems = useMemo<TransferItem[]>(() => zoneItems.filter((item) => formData.zoneSelect.includes(item.id)), [zoneItems, formData.zoneSelect]).sort((a, b) =>
        a.name.localeCompare(b.name),
    );

    const selectedScheduleDesviationItems = useMemo<TransferItem[]>(
        () => scheduleDesviationItems.filter((item) => formData.scheduleDesviationSelect.includes(item.id)),
        [scheduleDesviationItems, formData.scheduleDesviationSelect],
    ).sort((a, b) => a.name.localeCompare(b.name));

    // Verificar permisos
    const permissions = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('permissions') || '[]') as string[];
        } catch (e) {
            console.error('Error parsing permissions from localStorage:', e);
            return [];
        }
    }, []);

    // Cargar datos
    useEffect(() => {
        const loadData = async <T extends keyof DataStates>(permission: string, dataType: T, fetchFunction: () => Promise<DataStates[T]>) => {
            if (!permissions.includes(permission)) {
                setLoadingStates((prev) => ({ ...prev, [dataType]: false }));
                return;
            }

            setLoadingStates((prev) => ({ ...prev, [dataType]: true }));

            try {
                const result = await fetchFunction();
                setDataStates((prev) => ({ ...prev, [dataType]: result }));
            } catch (error) {
                console.error(`Error loading ${dataType}:`, error);
                setDataStates((prev) => ({ ...prev, [dataType]: [] as DataStates[T] }));
            } finally {
                setLoadingStates((prev) => ({ ...prev, [dataType]: false }));
            }
        };

        // Cargar datos en paralelo siempre
        Promise.all([
            loadData('timekeeper.OvertimeRules', 'overtime_rules', () => tk.overtime.getOvertimes()),
            loadData('timekeeper.Zones', 'zones', () => tk.zones.getZones()),
            loadData('timekeeper.ScheduleDesviationRule', 'schedule_desviation_rules', () => tk.schedule_desviation.getScheduleDesviations()),
        ]);
    }, [permissions]);

    // Inicializar formulario según el modo
    useEffect(() => {
        if ((mode === 'edit' || mode === 'duplicate') && select) {
            setFormData((prev) => ({
                ...prev,
                combination_rule_id: mode === 'edit' ? select.id : select?.id,
                name: mode === 'duplicate' ? '' : select.name || '',
                description: select.description || '',
                overtimeSelect: select.overtime_rules?.map((o) => o.overtime_rule_id) || [],
                zoneSelect: select.zones?.map((z) => z.zone_id) || [],
                scheduleDesviationSelect: select.schedule_desviation_rules?.map((s) => s.schedule_desviation_rule_id) || [],
                overtime_rules: [],
                zones: [],
                schedule_desviation_rules: [],
            }));
        }
    }, [mode, select, setFormData]);

    // Actualizar selecciones cuando cambian los datos
    useEffect(() => {
        if (dataStates.overtime_rules.length > 0) {
            const selectedOvertimes = formData.overtimeSelect || [];
            const overtimeRules = selectedOvertimes
                .map((overtime_rule_id) => {
                    const overtime = dataStates.overtime_rules.find((ot) => ot.overtime_rule_id === overtime_rule_id);
                    return overtime ? { overtime_rule_id: overtime.overtime_rule_id, name: overtime.name } : null;
                })
                .filter(Boolean) as OvertimeRule[];

            setFormData((prev) => ({ ...prev, overtime_rules: overtimeRules }));
        }

        if (dataStates.zones.length > 0) {
            const selectedZones = formData.zoneSelect || [];
            const zones = selectedZones
                .map((zone_id) => {
                    const zone = dataStates.zones.find((z) => z.zone_id === zone_id);
                    return zone ? { zone_id: zone.zone_id, name: zone.name } : null;
                })
                .filter(Boolean) as Zone[];

            setFormData((prev) => ({ ...prev, zones: zones }));
        }

        if (dataStates.schedule_desviation_rules.length > 0) {
            const selectedScheduleDesviations = formData.scheduleDesviationSelect || [];
            const scheduleDesviationRules = selectedScheduleDesviations
                .map((schedule_desviation_rule_id) => {
                    const scheduleDesviation = dataStates.schedule_desviation_rules.find((sd) => sd.schedule_desviation_rule_id === schedule_desviation_rule_id);
                    return scheduleDesviation
                        ? {
                              schedule_desviation_rule_id: scheduleDesviation.schedule_desviation_rule_id,
                              name: scheduleDesviation.name,
                          }
                        : null;
                })
                .filter(Boolean) as ScheduleDesviationRule[];

            setFormData((prev) => ({ ...prev, schedule_desviation_rules: scheduleDesviationRules }));
        }
    }, [dataStates, formData.overtimeSelect, formData.zoneSelect, formData.scheduleDesviationSelect, setFormData]);

    // Notificar cuando los datos estén cargados
    useEffect(() => {
        const allLoaded = !loadingStates.overtime_rules && !loadingStates.zones && !loadingStates.schedule_desviation_rules;
        if (allLoaded && onDataLoaded) {
            onDataLoaded({
                overtime_rules: dataStates.overtime_rules,
                zones: dataStates.zones,
                schedule_desviation_rules: dataStates.schedule_desviation_rules,
            });
        }
    }, [loadingStates, dataStates, onDataLoaded]);

    const existName = (name: string): boolean => {
        if (!name || name.trim() === '') return false;

        // Si estamos editando y el nombre no ha cambiado del original
        if (mode === 'edit' && select && String(select.name).toLowerCase() === String(name).toLowerCase().trim()) {
            return false;
        }

        // Verifica si existe en otros registros
        return rows.some((row) => String(row.name).toLowerCase() === String(name).toLowerCase().trim());
    };

    const handleNameChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value;
        setFormData((prev) => ({
            ...prev,
            name: value,
        }));
    };

    const handleDescriptionChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = e.target.value;
        setFormData((prev) => ({
            ...prev,
            description: value,
        }));
    };

    const handleOvertimeSelectChange = (selected: TransferItem[]): void => {
        setFormData((prev) => ({
            ...prev,
            overtimeSelect: selected.map((item) => item.id),
        }));
    };

    const handleZoneSelectChange = (selected: TransferItem[]): void => {
        setFormData((prev) => ({
            ...prev,
            zoneSelect: selected.map((item) => item.id),
        }));
    };

    const handleScheduleDesviationSelectChange = (selected: TransferItem[]): void => {
        setFormData((prev) => ({
            ...prev,
            scheduleDesviationSelect: selected.map((item) => item.id),
        }));
    };

    return (
        <>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <TextField
                            id="name"
                            name="name"
                            required
                            fullWidth
                            value={formData.name || ''}
                            autoComplete="off"
                            disabled={loadingStates.overtime_rules || loadingStates.zones || loadingStates.schedule_desviation_rules}
                            onChange={handleNameChange}
                            label={t('settings.combinations.dialog.inputs.name')}
                            slotProps={{
                                htmlInput: { maxLength: 100 },
                                input: {
                                    endAdornment: <InputAdornment position="end">{`${(formData.name || '').length}/100`}</InputAdornment>,
                                },
                            }}
                            error={existName(formData.name)}
                        />
                        <FormHelperText
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mt: 0.5,
                                px: 1.75,
                            }}
                        >
                            <span>{existName(formData.name) ? t('settings.combinations.dialog.inputs.exist_name') : ''}</span>
                        </FormHelperText>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <TextField
                            id="description"
                            fullWidth
                            name="description"
                            label={t('settings.combinations.dialog.inputs.description')}
                            placeholder={t('settings.combinations.dialog.inputs.max_description')}
                            value={formData.description || ''}
                            autoComplete="off"
                            disabled={loadingStates.overtime_rules || loadingStates.zones || loadingStates.schedule_desviation_rules}
                            onChange={handleDescriptionChange}
                            multiline
                            rows={2}
                            slotProps={{
                                htmlInput: { maxLength: 255 },
                                input: {
                                    endAdornment: <InputAdornment position="end">{`${(formData.description || '').length}/255`}</InputAdornment>,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <InputLabel>{t('settings.combinations.dialog.inputs.overtime_rules')}</InputLabel>
                        <Divider sx={{ mb: 1 }} />
                        <AssignList
                            loading={loadingStates.overtime_rules}
                            allItems={overtimeItems}
                            right={selectedOvertimeItems}
                            setRight={handleOvertimeSelectChange}
                            textLeft={t('general.transfer_list.available')}
                            textRight={t('general.transfer_list.selected')}
                            textSelected={t('general.transfer_list.selected')}
                        />
                        <Divider sx={{ mt: 1 }} />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <InputLabel sx={{ my: 1.5 }}>{t('settings.combinations.dialog.inputs.zones_rules')}</InputLabel>
                        <Divider sx={{ mb: 1 }} />
                        <AssignList
                            loading={loadingStates.zones}
                            allItems={zoneItems}
                            right={selectedZoneItems}
                            setRight={handleZoneSelectChange}
                            textLeft={t('general.transfer_list.available')}
                            textRight={t('general.transfer_list.selected')}
                            textSelected={t('general.transfer_list.selected')}
                        />
                        <Divider sx={{ mt: 1 }} />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <InputLabel sx={{ my: 1.5 }}>{t('settings.combinations.dialog.inputs.schedule_desviation_rules')}</InputLabel>
                        <Divider sx={{ mb: 1 }} />
                        <AssignList
                            loading={loadingStates.schedule_desviation_rules}
                            allItems={scheduleDesviationItems}
                            right={selectedScheduleDesviationItems}
                            setRight={handleScheduleDesviationSelectChange}
                            textLeft={t('general.transfer_list.available')}
                            textRight={t('general.transfer_list.selected')}
                            textSelected={t('general.transfer_list.selected')}
                        />
                        <Divider sx={{ mt: 1 }} />
                    </Grid>
                </Grid>
            </DialogContent>
        </>
    );
};

export default CombinationRuleForm;
